/**
 * Time module.
 * @module src/time
 */
import { isBlank } from './any';
/**
 * Converts seconds to milliseconds.
 *
 * @function seconds
 *
 * @param {number} seconds - the seconds to be converted
 * @return {number} the seconds represented in milliseconds
 *
 * Examples:
 *
 * @example
 * seconds(10);
 * // returns 10000
 *
 */

export const seconds = seconds => seconds * 1000;
/**
 * Converts minutes to milliseconds.
 *
 * @function minutes
 *
 * @param {number} minutes - the minutes to be converted
 * @return {Number} the minutes represented in milliseconds
 *
 * Examples:
 *
 * @example
 * minutes(10);
 * // return 600000
 *
 */

export const minutes = minutes => minutes * 60 * 1000;
/**
 * Converts hours to minutes.
 *
 * @function hoursToMinutes
 *
 * @param {Number} hours - the hours to be converted
 * @return {Number} the hours converted to minutes
 *
 * Examples:
 *
 * @example
 * hoursToMinutes(3.5);
 * // return 210
 *
 */

export const hoursToMinutes = hours => hours * 60;

const secondsToMilliseconds = seconds => seconds * 1000;

const minutesToMilliseconds = minutes => secondsToMilliseconds(minutes * 60);

const hoursToMilliseconds = hours => minutesToMilliseconds(hours * 60);

const daysToMilliseconds = days => hoursToMilliseconds(days * 24);

const toMillisecondsConversions = {
  milliseconds: milliseconds => milliseconds,
  seconds: secondsToMilliseconds,
  minutes: minutesToMilliseconds,
  hours: hoursToMilliseconds,
  days: daysToMilliseconds
};
/**
 * Converts some unit interval to milliseconds.
 *
 * @function toMilliseconds
 *
 * @param {Number} interval - the interval to be converted
 * @param {('milliseconds'|'seconds'|'minutes'|'hours'|'days')} timeUnit - the time unit to be converted
 * @return {Number} the interval converted to milliseconds
 *
 * Examples:
 *
 * @example
 * toMilliseconds(3.5, 'hours');
 * // return 12600000
 *
 */

export const toMilliseconds = (interval, timeUnit) => {
  const conversion = toMillisecondsConversions[timeUnit];
  if (isBlank(conversion)) throw new Error(`The unit '${timeUnit}' is not in the list [${Object.keys(toMillisecondsConversions).join(', ')}].`);
  return conversion(interval);
};
/* Units of seconds conversion */

const millisecondsToSeconds = milliseconds => Math.floor(milliseconds / 1000);

const minutesToSeconds = minutes => minutes * 60;

const hoursToSeconds = hours => minutesToSeconds(hours * 60);

const daysToSeconds = days => hoursToSeconds(days * 24);

const toSecondsConversions = {
  milliseconds: millisecondsToSeconds,
  seconds: seconds => seconds,
  minutes: minutesToSeconds,
  hours: hoursToSeconds,
  days: daysToSeconds
};
/**
 * Converts some unit interval to seconds.
 *
 * @function toSeconds
 *
 * @param {Number} interval - the interval to be converted
 * @param {('milliseconds'|'seconds'|'minutes'|'hours'|'days')} timeUnit - the time unit to be converted
 * @return {Number} the interval converted to seconds
 *
 * Examples:
 *
 * @example
 * toSeconds(3.5, 'hours');
 * // return 12600000
 *
 */

export const toSeconds = (interval, timeUnit) => {
  const conversion = toSecondsConversions[timeUnit];
  if (isBlank(conversion)) throw new Error(`The unit '${timeUnit}' is not in the list [${Object.keys(toSecondsConversions).join(', ')}].`);
  return conversion(interval);
};